import { useEffect, useState } from 'react';
import {
  DEFAULT_KEY_BINDINGS,
  DEFAULT_SETTINGS,
  parseStoredSettings,
  rebindKey,
  type AppSettings,
  type GameAction,
} from '../settings/settings';

const SETTINGS_KEY = 'codytris-settings-v1';

function readSettings(): AppSettings {
  try {
    return parseStoredSettings(JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? 'null'));
  } catch {
    return parseStoredSettings(null);
  }
}

export interface SettingsControls {
  setKeyBinding: (action: GameAction, binding: string) => void;
  resetKeyBinding: (action: GameAction) => void;
  setShowGhost: (enabled: boolean) => void;
  setMotionEffects: (enabled: boolean) => void;
  setTouchHaptics: (enabled: boolean) => void;
  restoreDefaults: () => void;
}

export function useSettings(): { settings: AppSettings; settingsControls: SettingsControls } {
  const [settings, setSettings] = useState<AppSettings>(readSettings);

  useEffect(() => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch {
      // Settings remain active for the current session when storage is unavailable.
    }
  }, [settings]);

  const settingsControls: SettingsControls = {
    setKeyBinding: (action, binding) => setSettings((current) => ({
      ...current,
      keyBindings: rebindKey(current.keyBindings, action, binding),
    })),
    resetKeyBinding: (action) => setSettings((current) => ({
      ...current,
      keyBindings: rebindKey(current.keyBindings, action, DEFAULT_KEY_BINDINGS[action]),
    })),
    setShowGhost: (showGhost) => setSettings((current) => ({ ...current, showGhost })),
    setMotionEffects: (motionEffects) => setSettings((current) => ({ ...current, motionEffects })),
    setTouchHaptics: (touchHaptics) => setSettings((current) => ({ ...current, touchHaptics })),
    restoreDefaults: () => setSettings({ ...DEFAULT_SETTINGS, keyBindings: { ...DEFAULT_KEY_BINDINGS } }),
  };

  return { settings, settingsControls };
}
